import { type SeoMeta } from '@/types';
import { Head, usePage } from '@inertiajs/react';
import type { SharedData } from '@/types';

interface Props {
  seoMeta?: SeoMeta | null;
  /** Fallback title when no seoMeta is provided */
  title?: string;
}

/**
 * Comprehensive SEO head component.
 *
 * Renders title, description, keywords, canonical, robots, Open Graph,
 * Twitter Card, and additional meta — all populated from the page's
 * seoMeta prop with brand-level fallbacks from shared Inertia props.
 */
export function PageHead({ seoMeta, title: titleFallback }: Props) {
  const { brand } = usePage<SharedData>().props;
  const brandSeo = (brand as any)?.seo ?? {};

  const siteName   = brand?.name ?? '';
  const twitterSite = brandSeo?.twitter_site ?? null;
  const defaultOgImage = brandSeo?.og_image ?? null;
  const brandKeywords: string = brandSeo?.keywords ?? '';

  const resolvedTitle       = seoMeta?.title ?? titleFallback ?? siteName;
  const resolvedDescription = seoMeta?.description ?? brand?.tagline ?? '';
  const resolvedCanonical   = seoMeta?.canonical ?? null;
  const resolvedOgTitle     = seoMeta?.og_title ?? resolvedTitle;
  const resolvedOgDesc      = seoMeta?.og_description ?? resolvedDescription;
  const resolvedOgImage     = seoMeta?.og_image ?? defaultOgImage;
  const resolvedOgType      = seoMeta?.og_type ?? 'website';
  const resolvedNoindex     = seoMeta?.noindex ?? false;
  const resolvedLocale      = brand?.default_locale ?? 'en';

  const pageKeywords: string[] = seoMeta?.keywords ?? [];
  const brandKwArray = brandKeywords
    ? brandKeywords.split(',').map((k: string) => k.trim()).filter(Boolean)
    : [];
  const allKeywords = [...new Set([...pageKeywords, ...brandKwArray])];

  return (
    <Head title={resolvedTitle}>
      {/* ── Basic ──────────────────────────────────────────────── */}
      {resolvedDescription && (
        <meta name="description" content={resolvedDescription} />
      )}
      {allKeywords.length > 0 && (
        <meta name="keywords" content={allKeywords.join(', ')} />
      )}
      {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}
      <meta
        name="robots"
        content={
          resolvedNoindex
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large'
        }
      />

      {/* ── Open Graph ─────────────────────────────────────────── */}
      <meta property="og:type" content={resolvedOgType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={resolvedLocale.replace('-', '_')} />
      {resolvedCanonical && (
        <meta property="og:url" content={resolvedCanonical} />
      )}
      <meta property="og:title" content={resolvedOgTitle} />
      {resolvedOgDesc && (
        <meta property="og:description" content={resolvedOgDesc} />
      )}
      {resolvedOgImage && [
        <meta key="og:image" property="og:image" content={resolvedOgImage} />,
        <meta
          key="og:image:alt"
          property="og:image:alt"
          content={resolvedOgTitle}
        />,
        <meta key="og:image:width" property="og:image:width" content="1200" />,
        <meta key="og:image:height" property="og:image:height" content="630" />,
      ]}

      {/* ── Twitter / X Card ───────────────────────────────────── */}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      <meta name="twitter:title" content={resolvedOgTitle} />
      {resolvedOgDesc && (
        <meta name="twitter:description" content={resolvedOgDesc} />
      )}
      {resolvedOgImage && (
        <meta name="twitter:image" content={resolvedOgImage} />
      )}

      {/* ── Theme / PWA ────────────────────────────────────────── */}
      {brand?.primary_color && (
        <meta name="theme-color" content={brand.primary_color} />
      )}
    </Head>
  );
}
